/* ============================================================================
   Admin module — the Admin section shell with sub-tabs:
   Users & Roles · Security policy · Notifications · Audit log.
   Uses the shared .tabs/.tab classes. The active tab is remembered in the URL
   hash so a refresh stays put. Route is guarded to Admin / SystemAdmin.
   ============================================================================ */

import { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api';
import UsersPanel from './UsersPanel';
import SettingsPanel from './SettingsPanel';
import AuditPanel from './AuditPanel';
import AccessRequestsPanel from './AccessRequestsPanel';

type TabKey = 'users' | 'access' | 'security' | 'notifications' | 'audit';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'users', label: 'Users & Roles' },
  { key: 'access', label: 'Access Requests' },
  { key: 'security', label: 'Security policy' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'audit', label: 'Audit log' },
];

function tabFromHash(): TabKey {
  const h = window.location.hash.replace('#', '') as TabKey;
  return TABS.some((t) => t.key === h) ? h : 'users';
}

export default function AdminArea() {
  const [tab, setTab] = useState<TabKey>(tabFromHash);
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  useEffect(() => {
    const onHash = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    // Best-effort pending-count badge — refreshed whenever the Access
    // Requests tab is (re)entered, so approvals/rejections stay reflected.
    apiGet<unknown[]>('/api/access-requests?status=pending')
      .then((rows) => setPendingCount(Array.isArray(rows) ? rows.length : null))
      .catch(() => setPendingCount(null));
  }, [tab]);

  function select(key: TabKey) {
    setTab(key);
    window.location.hash = key;
  }

  return (
    <div className="container page">
      <div className="page-title-row">
        <div>
          <p className="eyebrow">Administration</p>
          <h1>Admin</h1>
        </div>
      </div>

      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`tab ${tab === t.key ? 'tab-active' : ''}`}
            onClick={() => select(t.key)}
          >
            {t.label}
            {t.key === 'access' && !!pendingCount && (
              <span className="badge-count" style={{ marginLeft: 6 }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'users' && <UsersPanel />}
      {tab === 'access' && <AccessRequestsPanel />}
      {tab === 'security' && (
        <SettingsPanel
          category="security"
          title="Security policy"
          intro="Password requirements, account lockout, and session timeout. Applied when users are invited and when passwords are set."
        />
      )}
      {tab === 'notifications' && (
        <SettingsPanel
          category="notifications"
          title="Notification preferences"
          intro="Control which workflow events trigger notifications."
        />
      )}
      {tab === 'audit' && <AuditPanel />}
    </div>
  );
}
